import React, { useState } from 'react';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import { cleaningFeeSchema, ICleaningFee } from '../Interfaces/ICleaningFees';
import { useCreateCleaningFee, useUpdateCleaningFee } from '../hooks/cleaningFees';
import { useAlert } from '../core/AlertContext';
import { fieldSx, primaryButtonSx, secondaryButtonSx } from '../core/pvtaStyles';

interface CleaningFeeEditProps {
    initialCleaningFee: ICleaningFee;
    onClose: () => void;
    showHeading?: boolean;
}

const CleaningFeeEdit: React.FC<CleaningFeeEditProps> = ({ initialCleaningFee, onClose, showHeading = true }) => {
    const [cleaningFee, setCleaningFee] = useState<ICleaningFee>(initialCleaningFee);
    const isCreateMode = initialCleaningFee.id < 0;
    const { createCleaningFee, isCreatingCleaningFee } = useCreateCleaningFee();
    const { updateCleaningFee, isUpdatingCleaningFee } = useUpdateCleaningFee();
    const { showAlert } = useAlert();
    const isSaving = isCreatingCleaningFee || isUpdatingCleaningFee;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCleaningFee((prev) => ({
            ...prev,
            [name]: name === 'price' ? Number(value) : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const payload = cleaningFeeSchema.pick({
                name: true,
                price: true,
            }).parse(cleaningFee);

            if (isCreateMode) {
                await createCleaningFee(payload);
                showAlert({ text: 'Reinigungspauschale erfolgreich angelegt.' });
            } else {
                await updateCleaningFee({ ...cleaningFee, ...payload });
                showAlert({ text: 'Reinigungspauschale erfolgreich aktualisiert.' });
            }
            onClose();
        } catch {
            showAlert({
                text: isCreateMode
                    ? 'Fehler beim Anlegen der Reinigungspauschale.'
                    : 'Fehler beim Aktualisieren der Reinigungspauschale.',
                severity: 'error',
            });
        }
    };

    return (
        <Box sx={{ minWidth: 0 }}>
            <form onSubmit={handleSubmit}>
                {showHeading && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Details
                    </Typography>
                )}
                <TextField
                    name="name"
                    label="Name"
                    value={cleaningFee.name}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={{ ...fieldSx }}
                />
                <TextField
                    name="price"
                    label="Preis"
                    type="number"
                    value={cleaningFee.price}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ ...fieldSx }}
                />
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1.5, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                    <Button onClick={onClose} sx={{ ...secondaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        Schließen
                    </Button>
                    <Button type="submit" disabled={isSaving} sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        {isSaving ? 'Speichern...' : 'Speichern'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default CleaningFeeEdit;
